import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewsletterPopup — professional, non-intrusive scroll-triggered signup.
 * Appears once per visitor (localStorage) after 50% scroll on any page it's
 * mounted on. Skips /admin and /portal (auth flows) automatically.
 *
 * Props:
 *   brand: "collective" | "careers" (styles + which list to add to)
 *   source: string — where the popup was shown (e.g. "landing", "guides")
 */
export default function NewsletterPopup({ brand = "collective", source = "landing" }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isEmployer, setIsEmployer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const KEY = `tdc_newsletter_shown_${brand}`;

  // Skip on auth pages so we never interrupt a login / portal flow.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const skip = path.startsWith("/admin")
    || path.startsWith("/portal")
    || path.startsWith("/members")
    || path.startsWith("/auth")
    || path.startsWith("/resume-review/intake")
    || path.startsWith("/pet-resume");

  useEffect(() => {
    if (skip) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(KEY)) return;

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      // Fire at 25% scroll (was 50%) — user asked for a short delay.
      if (totalHeight > 0 && scrolled / totalHeight >= 0.25) {
        setOpen(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also open after 15s dwell on-page (was 30s) so short pages get a chance.
    const timer = setTimeout(() => {
      if (!localStorage.getItem(KEY)) { setOpen(true); }
    }, 15000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [skip, KEY]);

  const dismiss = () => {
    setDismissed(true);
    setOpen(false);
    try { localStorage.setItem(KEY, "1"); } catch (_) {}
  };

  const isCareers = brand === "careers";

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Pop in a valid email."); return; }
    setSubmitting(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanName = firstName.trim();
      await axios.post(`${API}/marketing/subscribe`, {
        email: cleanEmail,
        first_name: cleanName,
        source: isEmployer ? `${source}:employer` : source,
        brand,
        is_employer: isEmployer,
      });
      try { localStorage.setItem(KEY, "1"); } catch (_) {}
      // Smart Redirect Funnel — both brands hand off to a full intake so
      // we capture the deeper detail while the subscriber is warm.
      // Collective → /move · Careers → /roles
      const targetPath = isCareers ? "/roles" : "/move";
      const toastMsg = isCareers
        ? "You're in. Now tell us about the roles you want →"
        : "You're on the list. Now let's grab your move details →";
      try {
        sessionStorage.setItem("tdc_prefill", JSON.stringify({
          email: cleanEmail, first_name: cleanName,
        }));
      } catch (_) {}
      toast.success(toastMsg);
      const params = new URLSearchParams();
      if (cleanEmail) params.set("email", cleanEmail);
      if (cleanName) params.set("first_name", cleanName);
      params.set("src", "newsletter_popup");
      // Small delay so the toast is visible before the nav.
      setTimeout(() => {
        window.location.href = `${targetPath}?${params.toString()}`;
      }, 600);
      return;
    } catch (err) {
      toast.error("Couldn't save that just now — try again in a bit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || dismissed || skip) return null;

  const bg = "#F5F3EE";
  const primary = "#0D2F4D";
  const accent = "#C6D21F";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-popup-title"
      data-testid={`newsletter-popup-${brand}`}
      className="fixed z-[9998] bottom-6 right-6 max-w-[380px] w-[calc(100%-3rem)] shadow-2xl rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ background: bg, border: `1px solid ${primary}22` }}
    >
      <button
        onClick={dismiss}
        aria-label="Close"
        data-testid="newsletter-popup-close"
        className="absolute top-3 right-3 text-[#6F8FA8] hover:text-[#0D2F4D] z-10"
      >
        <X size={18} />
      </button>

      <div className="p-6" style={{ color: primary }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: accent }}>
            <Mail size={16} style={{ color: primary }} />
          </div>
          <span className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">
            {isCareers ? "Career updates" : "The monthly ditch"}
          </span>
        </div>
        <h3 id="newsletter-popup-title" className="text-2xl font-serif italic leading-tight mb-2" style={{ fontFamily: "Georgia, serif" }}>
          {isCareers
            ? "Fresh roles across the ditch."
            : "The move, in your inbox."}
        </h3>
        <p className="text-sm mb-4 leading-relaxed">
          {isCareers
            ? "New Trans-Tasman roles + interview tips + relocation salary intel. One email a month, no fluff."
            : "One monthly note — practical Kiwi-to-Australia moving advice, real client wins, no spam. Unsubscribe any time."}
        </p>

        <form onSubmit={submit} className="space-y-2" data-testid="newsletter-popup-form">
          <input
            type="text"
            placeholder="First name (optional)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            data-testid="newsletter-popup-firstname"
            className="w-full px-3 py-2 text-sm rounded-lg border border-[#e2dccd] bg-white outline-none focus:border-[#0D2F4D]"
          />
          <input
            type="email"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="newsletter-popup-email"
            className="w-full px-3 py-2 text-sm rounded-lg border border-[#e2dccd] bg-white outline-none focus:border-[#0D2F4D]"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="newsletter-popup-submit"
            className="w-full py-2.5 rounded-full text-sm font-bold transition-colors disabled:opacity-60"
            style={{ background: accent, color: primary }}
          >
            {submitting ? "Adding you…" : (isCareers ? "Join & tell us your role search →" : "Join & tell us about your move →")}
          </button>
        </form>

        <label
          className="flex items-center gap-2 mt-3 text-[12px] text-[#3E5B75] cursor-pointer select-none"
          data-testid="newsletter-popup-employer-label"
        >
          <input
            type="checkbox"
            checked={isEmployer}
            onChange={(e) => setIsEmployer(e.target.checked)}
            data-testid="newsletter-popup-employer-checkbox"
            className="h-3.5 w-3.5 rounded border-[#C6CDD6] accent-[#0D2F4D]"
          />
          <span>I'm an employer looking to hire Kiwis across the ditch</span>
        </label>

        <p className="text-[11px] mt-3 text-[#6F8FA8]">
          By subscribing you agree to receive marketing emails. One-click unsubscribe on every one.
        </p>
      </div>
    </div>
  );
}
